import RequestService from "./request-service";

export default class LinkInterceptor {
  #$links;
  #pageOrigin;

  // onInterceptCallbacks is a map of arrays of callbacks for a particular url
  // The callbacks are called prior to sending the GET request.
  // Callbacks are passed the href of the request object and can edit the href
  // or return a falsey value to cancel the request
  #onInterceptCallbacks;

  // onResponseCallbacks is a map of arrays of callbacks for a particular url
  // The callbacks are called with a promise which resolves to the response of the GET request.
  #onResponseCallbacks;

  constructor() {
    const url = new URL(window.location.href);
    this.#pageOrigin = url.origin;
  }

  init() {
    this.#bindEvents();
    this.#onInterceptCallbacks = new Map();
    this.#onResponseCallbacks = new Map();
  }

  onIntercept(id, callback, ...hrefs) {
    for (const href of hrefs) {
      let registered = this.#onInterceptCallbacks.get(href) ?? new Map();
      // Replaces the callback if the id already exists
      registered.set(id, callback);
      this.#onInterceptCallbacks.set(href, registered);
    }
  }

  removeInterceptCallback(id, ...hrefs) {
    for (const href of hrefs) {
      const registered = this.#onInterceptCallbacks.get(href) ?? new Map();
      registered.delete(id);
    }
  }

  onResponse(id, callback, ...hrefs) {
    for (const href of hrefs) {
      const registered = this.#onResponseCallbacks.get(href) ?? new Map();
      // Replaces the callback if the id already exists
      registered.set(id, callback);
      this.#onResponseCallbacks.set(href, registered);
    }
  }

  removeResponseCallback(id, ...hrefs) {
    for (const href of hrefs) {
      const registered = this.#onResponseCallbacks.get(href) ?? new Map();
      registered.delete(id);
    }
  }

  rebindEvents() {
    this.#bindEvents();
  }

  #bindEvents() {
    this.#$links = $("a");

    // Ensure don't multi-bind
    this.#$links.off("click.link");
    this.#$links.off("keydown.link")

    this.#$links.on("click.link", (e) => this.#handleLinkClick(e));

    this.#$links.on("keydown.link", (e) => {
      if (e.keyCode === 13) {
        this.#handleLinkClick(e);
      }
    });
  }

  async #handleLinkClick(e) {
    const href = $(e.currentTarget).attr("href");
    // Ignore external links
    // The base param in the URL constructor is used only if the link is relative
    const linkUrl = new URL(href, this.#pageOrigin);
    if (linkUrl.origin !== this.#pageOrigin) {
      return;
    }

    const icb = this.#onInterceptCallbacks.get(href) ?? new Set();

    // Ignore links which have no intercept handlers
    if (icb.size == 0) {
      return;
    }

    e.preventDefault();

    // Call the registered intercept callbacks
    // and GET the response from the (possibly altered) url.
    // Cache the response promises, so multiple handlers don't cause multiple requests.
    const cachedResponses = new Map();
    for (const kvp of icb) {
      // const id = kvp[0]; // unused
      const cb = kvp[1]; // value
      const newHref = await cb(href);
      if (newHref) {
        let responseProm = cachedResponses.get(newHref);
        if (!responseProm) {
          responseProm = RequestService.get(newHref);
          cachedResponses.set(newHref, responseProm);
        }
      }
    }

    // For each response to a different href
    // call the registered response callbacks
    for (const [href, response] of cachedResponses) {
      const rcb = this.#onResponseCallbacks.get(href) ?? new Set();
      for (const kvp of rcb) {
        // const id = kvp[0]; // unused
        const cb = kvp[1]; // value
        await cb({ href, response });
      }
    }

    // Ensure any new page links are intercepted
    Promise.all(cachedResponses.values())
      .then(() => {
        this.#bindEvents();
      })
  }

}
